import { Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import {
    AppUpdateService,
    ChangelogComponent,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    compareSemanticVersions,
    ConnectivityService,
    DateUtilities,
    DeviceLockManagementModalComponent,
    DeviceLockService,
    ModalClass,
    prettifyDeviceLockFingerprint,
    SideMenuComponent,
    SynchronizationService,
    UserMasterInstanceToken,
    UserMasterInterface,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import {
    faBan,
    faChevronDown,
    faChevronUp,
    faCodeBranch,
    faDesktop,
    faInfoCircle,
    faToolbox,
} from '@fortawesome/free-solid-svg-icons';
import { CHANGELOGS } from '../../../../../environments/changelogs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutomaticFormsHandoverService } from '../../../services/automatic-forms-handover/automatic-forms-handover.service';

@Component({
    selector: 'pace-information',
    templateUrl: './pace-information.component.html',
    styleUrls: ['./pace-information.component.scss'],
    standalone: false,
})
export class PaceInformationComponent implements OnInit {
    public readonly strings: PaceInformationStrings;
    public readonly isPaceOnlineUser: boolean;
    public readonly faChevronDown = faChevronDown;
    public readonly faChevronUp = faChevronUp;
    public readonly faInfoCircle = faInfoCircle;
    public readonly faCodeBranch = faCodeBranch;
    public readonly faToolbox = faToolbox;
    public readonly faDesktop = faDesktop;
    public readonly faBan = faBan;
    public readonly changelogs = CHANGELOGS.sort((a, b) => -compareSemanticVersions(a.version, b.version)).slice(0, 15);

    public isCollapsed = true;
    public isOnline$ = this.connectivityService.isOnline$;
    // Resolves to the side menu this component is projected into (it is declared as its content child)
    private readonly sideMenu = inject(SideMenuComponent, { optional: true });

    public constructor(
        private readonly translate: TranslateService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly synchronizationService: SynchronizationService,
        private readonly modalService: BsModalService,
        private readonly router: Router,
        private readonly deviceLockService: DeviceLockService,
        private readonly appUpdateService: AppUpdateService,
        private readonly destroyRef: DestroyRef,
        private readonly connectivityService: ConnectivityService,
        private readonly automaticFormsHandoverService: AutomaticFormsHandoverService,
    ) {
        this.strings = {
            gid: this.configService.getUserFrontId(),
            salesOrg: '',
            plant: '',
            plantSalesOrgDescription: '',
            appVersion: this.clientManager.getAppVersion(),
            environment: this.clientManager.getCurrentEnvironment(),
            targetBackend: this.configService.getBackendName(),
            deviceId: prettifyDeviceLockFingerprint(this.deviceLockService.deviceFingerprint),
        };

        this.isPaceOnlineUser = this.userMasterService.isPaceOnlineUser();
    }

    public ngOnInit(): void {
        this.strings.salesOrg = this.userMasterService.getSelectedUserModel().getSalesOrg();
        this.strings.plant = this.userMasterService.getSelectedUserModel().getPlant();
        this.strings.plantSalesOrgDescription = this.userMasterService.getSelectedUserModel().getDescription();
    }

    public getLastSync(): string {
        return this.synchronizationService.getLastSyncTime().isValid()
            ? DateUtilities.formatDate(this.synchronizationService.getLastSyncTime())
            : <string>this.translate.instant('FREQUENT_ACTIVITIES_FOOTER_NO_SYNC');
    }

    public toggleIsCollapsed(): void {
        this.isCollapsed = !this.isCollapsed;
    }

    public openPaceSpector(): void {
        this.sideMenu?.closeSideMenu();
        this.router.navigate(['main', 'pace-spector']).catch(console.error);
    }

    public openChangelog(): void {
        this.modalService.show(ChangelogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'changelog-modal-title',
            initialState: {
                changelogs: this.changelogs,
            },
        });
    }

    public releaseLock(): void {
        this.modalService.show(DeviceLockManagementModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'app-notification-modal-title',
            keyboard: false,
            closeInterceptor: () => {
                return new Promise((resolve) => {
                    this.deviceLockService.lockState$
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe((lockState) => {
                            if (lockState.ownedByMe) {
                                resolve();
                            }
                        });
                });
            },
            initialState: {
                releaseLockCleanupHook$: this.automaticFormsHandoverService.handOverFormsOnCheckout(),
            },
            ignoreBackdropClick: true,
        });
    }

    public checkForUpdates(): void {
        this.appUpdateService.checkForUpdates();
    }
}

interface PaceInformationStrings {
    gid: string;
    salesOrg: string;
    plant: string;
    plantSalesOrgDescription: string;
    appVersion: string;
    environment: string;
    targetBackend: string;
    deviceId: string;
}
